import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api, ApiError } from '../api.js';
import { useMe } from '../auth.js';
import { Button, ErrorText } from '../components/ui.js';
import { DELIVERY_STATUS, eur, period, STATEMENT_STATUS, usd, type DeliveryStatus, type StatementStatus } from '../format.js';
import { StatementDeliveries, StatementReview } from './StatementReview.js';
import { StatementInvoice } from './StatementInvoice.js';

interface Statement {
  id: string;
  partnerId: string;
  partnerName: string;
  isHouse: boolean;
  paymentId: string | null;
  paidOn: string | null;
  number: string | null;
  status: StatementStatus;
  coveredMonths: Array<{ year: number; month: number }>;
  partnerRevenueUsd: string;
  sharePercentOfPayment: string;
  paymentEur: string;
  costsEur: string;
  payoutEur: string;
  generatedAt: string;
  legacyStatementId: number | null;
  reversesStatementId: string | null;
  deliveryStatus?: DeliveryStatus | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  /** Lexware reports the partner's voucher paid on this date (STE-11). */
  partnerPaidOn?: string | null;
}
interface StatementDocument {
  id: string;
  kind: 'statement_pdf' | 'statement_xlsx';
  filename: string;
  sha256: string;
  sizeBytes: number;
  createdAt: string;
}
interface StatementDetail extends Statement {
  payment: { amountUsd: string | null; reversedAt: string | null; reversalReason: string | null } | null;
  reversedByStatementId: string | null;
  documents: StatementDocument[];
  lines: Array<{
    gameId: string; gameName: string; year: number; month: number; netUnits: number; netSalesUsd: string | null; revenueShareUsd: string; partnerPercent: string; partnerUsd: string;
  }>;
}

const FORMAT = { statement_pdf: 'pdf', statement_xlsx: 'xlsx' } as const;
const size = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

/** Files of the legacy app that no legacy statement claims; kept downloadable. */
function LegacyFiles() {
  const files = useQuery({ queryKey: ['statement-files'], queryFn: () => api<StatementDocument[]>('GET', '/api/statement-files') });
  if (!files.data || files.data.length === 0) return null;
  return (
    <div className="space-y-2">
      <h2 className="font-medium">Legacy statement files without a statement</h2>
      <ul className="text-sm">
        {files.data.map((f) => (
          <li key={f.id}>
            <a className="underline" href={`/api/statement-files/${f.id}`} download>{f.filename}</a> <span className="text-slate-500">({size(f.sizeBytes)})</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function StatementDocuments({ s }: { s: StatementDetail }) {
  const isAdmin = useMe().user?.role === 'admin';
  const client = useQueryClient();
  const generate = useMutation({
    mutationFn: () => api<{ documents: unknown[] }>('POST', `/api/statements/${s.id}/documents`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['statements'] }),
  });
  const canGenerate = isAdmin && s.status === 'draft' && !s.isHouse;
  return (
    <div className="space-y-2">
      <h2 className="font-medium">Documents</h2>
      {s.isHouse && <p className="text-sm text-slate-500">SilentFuture's own statement is an internal record; it gets no partner documents.</p>}
      {s.documents.length > 0 ? (
        <ul className="text-sm">
          {s.documents.map((d) => (
            <li key={d.id}>
              <a className="underline" href={`/api/statements/${s.id}/documents/${FORMAT[d.kind]}`} download>{d.filename}</a>{' '}
              <span className="text-slate-500">
                ({size(d.sizeBytes)}, {new Date(d.createdAt).toLocaleString('en-GB')}, SHA-256 <span className="font-mono">{d.sha256.slice(0, 12)}</span>)
              </span>
            </li>
          ))}
        </ul>
      ) : (
        !s.isHouse && <p className="text-sm text-slate-500">No documents yet.</p>
      )}
      {canGenerate && (
        <Button onClick={() => generate.mutate()} disabled={generate.isPending}>
          {generate.isPending ? 'Generating...' : s.documents.length > 0 ? 'Regenerate PDF and XLSX' : 'Generate PDF and XLSX'}
        </Button>
      )}
      {canGenerate && <p className="text-sm text-slate-500">Draft documents are previews marked DRAFT; the statement number is assigned at approval.</p>}
      {generate.isError && <ErrorText>{generate.error instanceof ApiError ? generate.error.message : 'The documents could not be generated'}</ErrorText>}
    </div>
  );
}

const covered = (months: Statement['coveredMonths']) =>
  months.length === 0 ? '-' : months.length === 1 ? period(months[0]!) : `${period(months[0]!)} - ${period(months[months.length - 1]!)}`;

export function StatementsPage() {
  const [status, setStatus] = useState<StatementStatus | ''>('');
  const statements = useQuery({
    queryKey: ['statements', status],
    queryFn: () => api<Statement[]>('GET', `/api/statements${status ? `?status=${status}` : ''}`),
  });
  return (
    <section className="space-y-6">
      <h1 className="text-xl font-semibold">Partner statements</h1>
      <p className="max-w-3xl text-sm text-slate-500">
        Drafts are created when a payment is entered. Open a draft to review it; approving assigns the number and mails the PDF and XLSX to the partner. Approved statements never change; a correction is a reversal statement. Legacy statements are kept as historical records.
      </p>
      <label className="block text-sm">
        <span className="mr-2 text-slate-600 dark:text-slate-400">Status</span>
        <select className="rounded border border-slate-300 bg-white px-2 py-1 dark:border-slate-700 dark:bg-slate-900" value={status} onChange={(e) => setStatus(e.target.value as StatementStatus | '')}>
          <option value="">All</option>
          {Object.entries(STATEMENT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </label>
      {statements.isError && <ErrorText>{statements.error instanceof ApiError ? statements.error.message : 'The statements could not be loaded'}</ErrorText>}
      <table className="text-sm">
        <thead className="text-slate-500">
          <tr><th className="pr-8 text-left">Partner</th><th className="pr-8 text-left">Covered months</th><th className="pr-8 text-left">Number</th><th className="pr-8 text-left">Status</th><th className="pr-8 text-left">Mail</th><th className="pr-8 text-right">Partner USD</th><th className="pr-8 text-right">Share</th><th className="text-right">Payout</th></tr>
        </thead>
        <tbody>
          {statements.data?.map((s) => (
            <tr key={s.id} className="border-t border-slate-200 dark:border-slate-800">
              <td className="pr-8"><Link to="/statements/$statementId" params={{ statementId: s.id }} className="underline">{s.partnerName}</Link>{s.reversesStatementId ? ' (reversal)' : ''}</td>
              <td className="pr-8">{covered(s.coveredMonths)}</td>
              <td className="pr-8">{s.number ?? '-'}</td>
              <td className={`pr-8 ${STATEMENT_STATUS[s.status].className}`}>{STATEMENT_STATUS[s.status].label}{s.status === 'draft' && s.rejectedAt ? ' (rejected)' : ''}</td>
              <td className={`pr-8 ${s.deliveryStatus ? DELIVERY_STATUS[s.deliveryStatus].className : ''}`}>{s.deliveryStatus ? DELIVERY_STATUS[s.deliveryStatus].label : '-'}</td>
              <td className="pr-8 text-right tabular-nums">{usd(s.partnerRevenueUsd)}</td>
              <td className="pr-8 text-right tabular-nums">{Number(s.sharePercentOfPayment).toFixed(2)}%</td>
              <td className="text-right tabular-nums">{eur(s.payoutEur)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {statements.data?.length === 0 && <p className="text-sm text-slate-500">No statements.</p>}
      <LegacyFiles />
    </section>
  );
}

export function StatementDetailPage({ statementId }: { statementId: string }) {
  const statement = useQuery({ queryKey: ['statements', 'detail', statementId], queryFn: () => api<StatementDetail>('GET', `/api/statements/${statementId}`) });
  const s = statement.data;
  if (statement.isError) return <ErrorText>{statement.error instanceof ApiError && statement.error.status === 404 ? 'No such statement' : 'The statement could not be loaded'}</ErrorText>;
  if (!s) return null;
  const row = (label: string, value: string) => (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </>
  );
  return (
    <section className="space-y-6">
      <p className="text-sm"><Link to="/statements" className="underline">All statements</Link></p>
      <h1 className="text-xl font-semibold">
        {s.partnerName} {covered(s.coveredMonths)}{s.reversesStatementId ? ' (reversal)' : ''}
      </h1>
      <dl className="grid max-w-2xl grid-cols-[14rem_1fr] gap-y-1 text-sm">
        {row('Status', STATEMENT_STATUS[s.status].label)}
        {s.deliveryStatus && row('Mail', DELIVERY_STATUS[s.deliveryStatus].label)}
        {row('Number', s.number ?? (s.isHouse ? '-' : 'assigned at approval'))}
        {row('Payment received', `${eur(s.paymentEur)}${s.paidOn ? ` on ${s.paidOn}` : ''}${s.payment?.amountUsd ? ` (${usd(s.payment.amountUsd)})` : ''}`)}
        {row('Costs', eur(s.costsEur))}
        {row('Partner revenue share', usd(s.partnerRevenueUsd))}
        {row('Share of the payment', `${Number(s.sharePercentOfPayment).toFixed(2)}%`)}
        {row('Payout', eur(s.payoutEur))}
        {s.partnerPaidOn && row('Paid to the partner', s.partnerPaidOn)}
        {row('Generated', new Date(s.generatedAt).toLocaleString('en-GB'))}
        {s.legacyStatementId !== null && row('Legacy statement', String(s.legacyStatementId))}
      </dl>
      {s.status === 'draft' && <StatementReview s={s} />}
      <StatementDocuments s={s} />
      {!s.isHouse && s.status !== 'draft' && s.status !== 'historical' && <StatementDeliveries s={s} />}
      {!s.isHouse && s.status !== 'draft' && s.status !== 'historical' && <StatementInvoice statementId={s.id} isHouse={s.isHouse} />}
      {s.reversesStatementId && (
        <p className="text-sm">Reverses <Link to="/statements/$statementId" params={{ statementId: s.reversesStatementId }} className="underline">the original statement</Link>.</p>
      )}
      {s.reversedByStatementId && (
        <p className="text-sm">
          Reversed by <Link to="/statements/$statementId" params={{ statementId: s.reversedByStatementId }} className="underline">a reversal statement</Link>
          {s.payment?.reversalReason ? `: ${s.payment.reversalReason}` : ''}.
        </p>
      )}
      {s.lines.length > 0 ? (
        <table className="text-sm">
          <thead className="text-slate-500">
            <tr><th className="pr-8 text-left">Month</th><th className="pr-8 text-left">Game</th><th className="pr-8 text-right">Net units</th><th className="pr-8 text-right">Net sales</th><th className="pr-8 text-right">Revenue share</th><th className="pr-8 text-right">Percent</th><th className="text-right">Partner USD</th></tr>
          </thead>
          <tbody>
            {s.lines.map((l) => (
              <tr key={`${l.gameId}-${l.year}-${l.month}`} className="border-t border-slate-200 dark:border-slate-800">
                <td className="pr-8">{period(l)}</td>
                <td className="pr-8">{l.gameName}</td>
                <td className="pr-8 text-right tabular-nums">{l.netUnits}</td>
                <td className="pr-8 text-right tabular-nums">{l.netSalesUsd === null ? '-' : usd(l.netSalesUsd)}</td>
                <td className="pr-8 text-right tabular-nums">{usd(l.revenueShareUsd)}</td>
                <td className="pr-8 text-right tabular-nums">{Number(l.partnerPercent).toFixed(2)}%</td>
                <td className="text-right tabular-nums">{usd(l.partnerUsd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-sm text-slate-500">No per-game lines: the legacy app did not store them.</p>
      )}
    </section>
  );
}
